import { Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { GrantLevel, Project } from "~/api/governanceTypes";
import {
  useAddProjectGroup,
  useAddProjectPerson,
  useGroups,
  useMembers,
  useProjectGroups,
  useProjectPeople,
  useRemoveProjectGroup,
  useRemoveProjectPerson,
} from "~/api/queries";
import { Row, Rows } from "~/features/automation/components";
import { Select, Tabs } from "~/features/settings/controls";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";

const PERMISSION_OPTIONS: GrantLevel[] = ["read", "read_write", "admin"];

function permissionLabel(level: GrantLevel, t: (key: string) => string): string {
  switch (level) {
    case "read_write":
      return t("Read & write");
    case "admin":
      return t("Admin");
    default:
      return t("Read only");
  }
}

function PeopleTab({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { data, isLoading } = useProjectPeople(project.id);
  const { data: members } = useMembers();
  const addPerson = useAddProjectPerson();
  const removePerson = useRemoveProjectPerson();
  const [userId, setUserId] = useState("");
  const [permission, setPermission] = useState<GrantLevel>("read");

  const existingIds = new Set((data?.memberships ?? []).map((m) => m.userId));
  const candidates = (members ?? []).filter((m) => !existingIds.has(m.id));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <Select
          label={t("Person")}
          fullWidth
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        >
          <option value="">{t("Choose someone")}</option>
          {candidates.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name || member.email}
            </option>
          ))}
        </Select>
        <Select
          value={permission}
          onChange={(event) => setPermission(event.target.value as GrantLevel)}
        >
          {PERMISSION_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {permissionLabel(level, t)}
            </option>
          ))}
        </Select>
        <Button
          variant="neutral"
          disabled={!userId || addPerson.isPending}
          onClick={() => {
            addPerson.mutate({ id: project.id, userId, permission });
            setUserId("");
          }}
        >
          {t("Add")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !data?.memberships.length ? (
        <Empty message={t("No people yet")} />
      ) : (
        <Rows>
          {data.memberships.map((membership) => {
            const member = data.users.find((u) => u.id === membership.userId);
            return (
              <Row key={membership.userId}>
                <Avatar name={member?.name ?? membership.userId} size={24} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">
                    {member?.name ?? membership.userId}
                  </p>
                  <p className="truncate text-small text-text-tertiary">{member?.email}</p>
                </div>
                <span className="text-small text-text-secondary">
                  {permissionLabel(membership.permission, t)}
                </span>
                <IconButton
                  icon={Trash}
                  aria-label={t("Remove")}
                  onClick={() => removePerson.mutate({ id: project.id, userId: membership.userId })}
                />
              </Row>
            );
          })}
        </Rows>
      )}
    </div>
  );
}

function TeamsTab({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { data: grants, isLoading } = useProjectGroups(project.id);
  const { data: groups } = useGroups();
  const addGroup = useAddProjectGroup();
  const removeGroup = useRemoveProjectGroup();
  const [groupId, setGroupId] = useState("");
  const [permission, setPermission] = useState<GrantLevel>("read");

  const existingIds = new Set((grants ?? []).map((g) => g.groupId));
  const candidates = (groups ?? []).filter((g) => !existingIds.has(g.id));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <Select
          label={t("Team")}
          fullWidth
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
        >
          <option value="">{t("Choose a team")}</option>
          {candidates.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>
        <Select
          value={permission}
          onChange={(event) => setPermission(event.target.value as GrantLevel)}
        >
          {PERMISSION_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {permissionLabel(level, t)}
            </option>
          ))}
        </Select>
        <Button
          variant="neutral"
          disabled={!groupId || addGroup.isPending}
          onClick={() => {
            addGroup.mutate({ id: project.id, groupId, permission });
            setGroupId("");
          }}
        >
          {t("Add")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !grants?.length ? (
        <Empty message={t("No teams granted yet")} />
      ) : (
        <Rows>
          {grants.map((grant) => (
            <Row key={grant.id}>
              <div className="min-w-0 flex-1 truncate text-body text-text">
                {grant.group?.name ?? grant.groupId}
              </div>
              <span className="text-small text-text-secondary">
                {permissionLabel(grant.permission, t)}
              </span>
              <IconButton
                icon={Trash}
                aria-label={t("Remove")}
                onClick={() => removeGroup.mutate({ id: project.id, groupId: grant.groupId })}
              />
            </Row>
          ))}
        </Rows>
      )}
    </div>
  );
}

export function ProjectPeopleDialog({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"people" | "teams">("people");

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("People")}
      description={project.name}
      closeLabel={t("Close")}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <Tabs
          value={tab}
          onChange={setTab}
          options={[
            { value: "people", label: t("People") },
            { value: "teams", label: t("Teams") },
          ]}
        />
        {tab === "people" ? <PeopleTab project={project} /> : <TeamsTab project={project} />}
      </div>
    </Dialog>
  );
}
